import React from 'react';

import { Participant, Vote } from '../utils/types';

interface ParticipantListProps {
    participants: { [userId: string]: Participant };
    currentVotes: { [userId: string]: Vote } | undefined;
    revealed: boolean;
}

const ParticipantList: React.FC<ParticipantListProps> = ({
    participants,
    currentVotes,
    revealed,
}) => {
    // Sort participants: admins first, then active users, then inactive users
    const sortedParticipants = Object.values(participants).sort((a, b) => {
        if (a.isAdmin && !b.isAdmin) return -1;
        if (!a.isAdmin && b.isAdmin) return 1;

        if (a.isActive && !b.isActive) return -1;
        if (!a.isActive && b.isActive) return 1;

        return a.name.localeCompare(b.name);
    });

    return (
        <div className="my-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-300 flex items-center">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                </svg>
                Participants ({sortedParticipants.length})
            </h3>

            <ul className="space-y-2">
                {sortedParticipants.map((participant) => {
                    const hasVoted =
                        currentVotes && currentVotes[participant.id];

                    return (
                        <li
                            key={participant.id}
                            className={`
                                flex items-center justify-between
                                p-3 rounded-lg transition-all duration-200
                                ${
                                    participant.isActive
                                        ? 'bg-white dark:bg-dark-card shadow-sm border border-gray-100 dark:border-dark-border'
                                        : 'bg-gray-100 dark:bg-gray-800 opacity-60'
                                }
                                ${participant.isAdmin ? 'border-l-4 border-l-primary-dark' : ''}
                            `}
                        >
                            <div className="flex items-center min-w-0 flex-1 mr-3">
                                <div
                                    className={`
                                    h-2 w-2 rounded-full mr-3 flex-shrink-0
                                    ${participant.isActive ? 'bg-green-500' : 'bg-gray-400 dark:bg-gray-600'}
                                `}
                                ></div>
                                <div className="min-w-0 flex-1">
                                    <span
                                        className="font-medium dark:text-dark-text block truncate"
                                        title={participant.name}
                                    >
                                        {participant.name}
                                    </span>
                                    {participant.isAdmin && (
                                        <span className="inline-block mt-1 text-xs bg-primary-light text-white px-2 py-0.5 rounded-full">
                                            Admin
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex-shrink-0">
                                {hasVoted ? (
                                    revealed ? (
                                        <span
                                            className={`
                                            inline-flex items-center justify-center
                                            h-8 min-w-8 px-2 rounded-full font-bold text-sm
                                            ${
                                                currentVotes[participant.id]
                                                    .value === '?'
                                                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                                    : 'bg-primary-light text-white'
                                            }
                                        `}
                                            title={
                                                currentVotes[participant.id]
                                                    .value
                                            }
                                        >
                                            {currentVotes[participant.id].value
                                                .length > 8
                                                ? currentVotes[
                                                      participant.id
                                                  ].value.substring(0, 8) +
                                                  '...'
                                                : currentVotes[participant.id]
                                                      .value}
                                        </span>
                                    ) : (
                                        <span className="flex items-center justify-center h-6 w-6 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-4 w-4"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        </span>
                                    )
                                ) : (
                                    <span className="flex items-center text-gray-400 dark:text-gray-500 text-sm whitespace-nowrap">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 mr-1"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                        waiting
                                    </span>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>

            {sortedParticipants.length === 0 && (
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">
                        No participants have joined yet.
                    </p>
                </div>
            )}
        </div>
    );
};

export default ParticipantList;
